import React, { useState, useEffect } from 'react';
import { X, Loader, Sparkles, AlertCircle } from 'lucide-react';
import AIWorkflowDiagram from './AIWorkflowDiagram';
import api from '../../api';
import toast from 'react-hot-toast';

interface AIWorkflowModalProps {
  opportunity: any;
  projectId: number;
  onClose: () => void;
}

const AIWorkflowModal: React.FC<AIWorkflowModalProps> = ({ opportunity, projectId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowData, setWorkflowData] = useState<any>(null);

  useEffect(() => {
    generateWorkflow();
  }, [opportunity.id]);

  const generateWorkflow = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚀 Generating AI workflow for opportunity:', opportunity.id);
      console.log('📦 Request data:', {
        opportunity: opportunity,
        processSteps: opportunity.affected_steps || [],
        projectId: projectId
      });

      const response = await api.post('/api/process-optimization/generate-ai-workflow', {
        opportunity: opportunity,
        processSteps: opportunity.affected_steps || [],
        projectId: projectId
      });

      console.log('✅ API Response:', response.data);

      if (response.data.success) {
        console.log('✅ Workflow data received:', response.data.workflow);
        setWorkflowData(response.data.workflow);
      } else {
        console.error('❌ API returned success=false:', response.data);
        throw new Error(response.data.message || 'Failed to generate workflow');
      }
    } catch (err: any) {
      console.error('❌ Error generating AI workflow:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Full error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate AI workflow';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    generateWorkflow();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Sparkles className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI-Powered Automation Workflow</h2>
              <p className="text-sm opacity-90 mt-1">
                Generated for: {opportunity.process_name || opportunity.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center h-96">
              <Loader className="w-16 h-16 text-purple-600 animate-spin mb-4" />
              <p className="text-gray-600 text-lg font-medium">Generating AI automation workflow...</p>
              <p className="text-gray-400 text-sm mt-2">
                Analyzing your process and creating step-by-step automation
              </p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-96">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
              <p className="text-gray-800 text-lg font-medium mb-2">Failed to generate workflow</p>
              <p className="text-gray-600 text-sm mb-6">{error}</p>
              <button
                onClick={handleRetry}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Retry Generation
              </button>
            </div>
          )}

          {workflowData && !loading && (
            <AIWorkflowDiagram workflowData={workflowData} />
          )}
        </div>

        {/* Footer with benefits */}
        {workflowData?.estimated_benefits && !loading && (
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold text-gray-600 mb-2">ESTIMATED BENEFITS</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {workflowData.estimated_benefits.time_savings && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Time Savings:</span> {workflowData.estimated_benefits.time_savings}
                  </span>
                </div>
              )}
              {workflowData.estimated_benefits.accuracy_improvement && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Accuracy:</span> {workflowData.estimated_benefits.accuracy_improvement}
                  </span>
                </div>
              )}
              {workflowData.estimated_benefits.automation_rate && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Automation:</span> {workflowData.estimated_benefits.automation_rate}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIWorkflowModal;
